<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Plus } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import PaginationControls from '../../components/PaginationControls.vue'
import { listCatalogProducts } from '../productService'
import {
  matchesProductDemographics,
  productDetailPath,
  resolveAudienceSegment,
} from '../productPresentation'
import type {
  CatalogKind,
  EngineeringProduct,
  ProductAgeGroup,
  ProductGender,
} from '../types'

const client = useApiClient()
const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()
const products = ref<EngineeringProduct[]>([])
const search = ref('')
const type = ref('')
const season = ref('')
const ageGroup = ref<ProductAgeGroup>('')
const gender = ref<ProductGender>('')
const readiness = ref('')
const loading = ref(false)
const errorMessage = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
let searchTimer: ReturnType<typeof setTimeout> | null = null
const catalogKind = computed<CatalogKind>(() =>
  route.meta.catalogKind === 'raw-materials' ||
  route.meta.catalogKind === 'spare-parts'
    ? route.meta.catalogKind
    : 'manufactured',
)
const isManufacturedCatalog = computed(
  () => catalogKind.value === 'manufactured',
)
const canManageProducts = computed(
  () => auth.user?.permissions.includes('products.manage') ?? false,
)
const pageCopy = computed(() => {
  if (catalogKind.value === 'raw-materials') {
    return {
      eyebrow: t('engineering.catalog.rawEyebrow'),
      title: t('engineering.catalog.rawTitle'),
      description: t('engineering.catalog.rawDescription'),
      createPath: '/raw-materials/new',
    }
  }
  if (catalogKind.value === 'spare-parts') {
    return {
      eyebrow: t('engineering.catalog.spareEyebrow'),
      title: t('engineering.catalog.spareTitle'),
      description: t('engineering.catalog.spareDescription'),
      createPath: '/spare-parts/new',
    }
  }
  return {
    eyebrow: t('engineering.catalog.manufacturedEyebrow'),
    title: t('engineering.catalog.manufacturedTitle'),
    description: t('engineering.catalog.manufacturedDescription'),
    createPath: '/products/new',
  }
})

const visibleProducts = computed(() => {
  let result = products.value
  if (isManufacturedCatalog.value) {
    result = result.filter((product) =>
      matchesProductDemographics(product, ageGroup.value, gender.value),
    )
  }
  if (readiness.value) {
    const isReady = readiness.value === 'ready'
    result = result.filter(
      (product) => Boolean(product.production_readiness?.is_ready) === isReady,
    )
  }
  return result
})

const readyCount = computed(
  () => products.value.filter((product) => product.production_readiness?.is_ready).length,
)
const totalPages = computed(() =>
  Math.max(1, Math.ceil(visibleProducts.value.length / pageSize.value)),
)
const paginatedProducts = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return visibleProducts.value.slice(start, start + pageSize.value)
})

async function loadProducts(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    products.value = await listCatalogProducts(client, catalogKind.value, {
      search: search.value.trim() || undefined,
      type: type.value || undefined,
      audience_segment: isManufacturedCatalog.value
        ? resolveAudienceSegment(ageGroup.value, gender.value)
        : undefined,
      season: season.value || undefined,
    })
  } catch (error: unknown) {
    products.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

function scheduleSearch(): void {
  currentPage.value = 1
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
  searchTimer = setTimeout(loadProducts, 300)
}

function applyFilters(): void {
  currentPage.value = 1
  if (searchTimer) {
    clearTimeout(searchTimer)
    searchTimer = null
  }
  void loadProducts()
}

onMounted(loadProducts)
watch(search, scheduleSearch)
watch([type, season, ageGroup, gender, readiness], () => {
  currentPage.value = 1
})
watch([visibleProducts, pageSize], () => {
  currentPage.value = Math.min(currentPage.value, totalPages.value)
})
watch(catalogKind, () => {
  search.value = ''
  type.value = ''
  season.value = ''
  ageGroup.value = ''
  gender.value = ''
  readiness.value = ''
  currentPage.value = 1
  void loadProducts()
})
watch(ageGroup, (value) => {
  if (value === 'kids' || value === 'teens') {
    gender.value = ''
  }
})
onBeforeUnmount(() => {
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ pageCopy.eyebrow }}</p>
      <h2>{{ pageCopy.title }}</h2>
      <p>{{ pageCopy.description }}</p>
    </div>
    <div class="heading-actions">
      <div v-if="isManufacturedCatalog" class="summary">
        <strong>{{ readyCount }}</strong>
        <span>{{ t('engineering.catalog.readyOf', { total: products.length }) }}</span>
      </div>
      <RouterLink
        v-if="canManageProducts"
        class="create-link"
        :to="pageCopy.createPath"
      >
        <Plus :size="16" aria-hidden="true" />
        {{ $t(`engineering.productForm.createAction.${catalogKind}`) }}
      </RouterLink>
      <span
        v-else
        class="create-link create-link--disabled"
        :title="$t('engineering.productForm.permissionHint')"
        :aria-label="$t('engineering.productForm.permissionHint')"
      >
        <Plus :size="16" aria-hidden="true" />
        {{ $t(`engineering.productForm.createAction.${catalogKind}`) }}
      </span>
    </div>
  </div>

  <form
    class="filters"
    :class="{ 'filters--compact': !isManufacturedCatalog }"
    @submit.prevent="applyFilters"
  >
    <label>
      <span>{{ t('engineering.catalog.search') }}</span>
      <input
        v-model="search"
        type="search"
        :placeholder="t('engineering.catalog.searchPlaceholder')"
      >
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.type') }}</span>
      <select v-model="type">
        <option value="">{{ t('engineering.catalog.allManufacturedTypes') }}</option>
        <option value="manufactured_good">{{ t('engineering.catalog.manufacturedGood') }}</option>
        <option value="finished_good">{{ t('engineering.catalog.finishedGood') }}</option>
        <option value="semi_finished_good">{{ t('engineering.catalog.semiFinishedGood') }}</option>
        <option value="semi_finished">{{ t('engineering.catalog.semiFinished') }}</option>
      </select>
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.season') }}</span>
      <select v-model="season">
        <option value="">{{ t('engineering.catalog.allSeasons') }}</option>
        <option value="winter">{{ t('engineering.catalog.winter') }}</option>
        <option value="summer">{{ t('engineering.catalog.summer') }}</option>
        <option value="all_season">{{ t('engineering.catalog.allSeason') }}</option>
      </select>
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.age') }}</span>
      <select v-model="ageGroup">
        <option value="">{{ t('engineering.catalog.allAges') }}</option>
        <option value="kids">{{ t('engineering.catalog.kids') }}</option>
        <option value="teens">{{ t('engineering.catalog.teens') }}</option>
        <option value="adult">{{ t('engineering.catalog.adults') }}</option>
      </select>
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.gender') }}</span>
      <select
        v-model="gender"
        :disabled="ageGroup === 'kids' || ageGroup === 'teens'"
      >
        <option value="">{{ t('engineering.catalog.allGenders') }}</option>
        <option value="men">{{ t('engineering.catalog.men') }}</option>
        <option value="women">{{ t('engineering.catalog.women') }}</option>
      </select>
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.readiness') }}</span>
      <select v-model="readiness">
        <option value="">{{ t('engineering.catalog.allStatuses') }}</option>
        <option value="ready">{{ t('engineering.catalog.ready') }}</option>
        <option value="not-ready">{{ t('engineering.catalog.notReady') }}</option>
      </select>
    </label>
    <button type="submit" :disabled="loading">{{ t('engineering.catalog.apply') }}</button>
  </form>

  <div v-if="errorMessage" class="message message--error" role="alert">
    <span>{{ errorMessage }}</span>
    <button type="button" @click="loadProducts">{{ t('engineering.action.retry') }}</button>
  </div>

  <div class="table-shell" :aria-busy="loading">
    <table>
      <thead>
        <tr>
          <th>{{ t('engineering.catalog.product') }}</th>
          <th>{{ t('engineering.catalog.classification') }}</th>
          <th>{{ t('engineering.catalog.unit') }}</th>
          <th v-if="isManufacturedCatalog">{{ t('engineering.catalog.readiness') }}</th>
          <th><span class="visually-hidden">{{ t('engineering.catalog.actions') }}</span></th>
        </tr>
      </thead>
      <tbody v-if="!loading && visibleProducts.length">
        <tr v-for="product in paginatedProducts" :key="product.id">
          <td>
            <strong>{{ product.name }}</strong>
            <span>{{ product.sku }}</span>
          </td>
          <td>
            {{ product.group?.name || product.type }}
            <span>{{ product.season || t('engineering.catalog.noSeason') }}</span>
          </td>
          <td>{{ product.unit }}</td>
          <td v-if="isManufacturedCatalog">
            <span
              class="status"
              :class="product.production_readiness?.is_ready
                ? 'status--ready'
                : 'status--blocked'"
            >
              {{ product.production_readiness?.is_ready ? t('engineering.catalog.ready') : t('engineering.catalog.notReady') }}
            </span>
            <small v-if="!product.production_readiness?.is_ready">
              {{ t('engineering.catalog.gaps', { count: product.production_readiness?.missing_requirements.length ?? 0 }) }}
            </small>
          </td>
          <td class="row-action">
            <RouterLink :to="productDetailPath(product)">{{ t('engineering.catalog.review') }}</RouterLink>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="loading" class="table-state">{{ t('engineering.catalog.loadingProducts') }}</div>
    <div
      v-else-if="!visibleProducts.length && !errorMessage"
      class="table-state"
    >
      {{ t('engineering.catalog.emptyProducts') }}
    </div>
    <PaginationControls
      v-if="!loading && visibleProducts.length"
      v-model:page="currentPage"
      v-model:page-size="pageSize"
      :total="visibleProducts.length"
      :page-size-options="[10, 25, 50]"
    />
  </div>
</template>

<style scoped>
.page-heading {
  display: flex;
  gap: 24px;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
}

.heading-actions {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.eyebrow {
  margin: 0 0 5px;
  color: #66726e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 26px;
  letter-spacing: 0;
}

.page-heading p:last-child {
  margin: 7px 0 0;
  color: #66726e;
}

.summary {
  display: grid;
  min-width: 112px;
  padding-left: 18px;
  border-left: 3px solid #3568a8;
}

.summary strong {
  font-size: 24px;
}

.summary span {
  color: #66726e;
  font-size: 12px;
}

.create-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #3568a8;
  border-radius: 8px;
  color: #ffffff;
  background: #3568a8;
  font-weight: 700;
  text-decoration: none;
}

.create-link--disabled {
  border-color: #b9c5c1;
  color: #6f7d78;
  background: #eef2f1;
  cursor: not-allowed;
}

.filters {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) repeat(5, minmax(120px, 150px)) auto;
  gap: 12px;
  align-items: end;
  padding: 16px;
  border: 1px solid #d7dfdc;
  background: #ffffff;
}

.filters--compact {
  grid-template-columns: minmax(240px, 1fr) auto;
}

label span {
  display: block;
  margin-bottom: 6px;
  color: #4e5d57;
  font-size: 12px;
  font-weight: 700;
}

input,
select,
button {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #adb9b4;
  border-radius: 8px;
  color: #17201d;
  background: #ffffff;
}

button {
  width: auto;
  cursor: pointer;
}

.filters > button {
  border-color: #3568a8;
  color: #ffffff;
  background: #3568a8;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.message {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #d7a8a1;
  color: #852d22;
  background: #fff5f3;
}

.table-shell {
  min-height: 240px;
  margin-top: 16px;
  overflow-x: auto;
  border: 1px solid #d7dfdc;
  background: #ffffff;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

th,
td {
  padding: 13px 14px;
  border-bottom: 1px solid #e1e6e4;
  text-align: left;
  vertical-align: middle;
}

th {
  color: #586660;
  background: #f3f6f5;
  font-size: 12px;
  text-transform: uppercase;
}

td strong,
td > span:not(.status),
td small {
  display: block;
}

td > span:not(.status),
td small {
  margin-top: 3px;
  color: #71807a;
  font-size: 12px;
}

.status {
  display: inline-block;
  width: max-content;
  padding: 4px 7px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.status--ready {
  border-color: #86b4a4;
  color: #236451;
  background: #eff8f5;
}

.status--blocked {
  border-color: #d2b37c;
  color: #765017;
  background: #fff8e9;
}

.row-action {
  width: 1%;
  white-space: nowrap;
}

.row-action a {
  color: #285b9a;
  font-weight: 700;
}

.table-state {
  display: grid;
  min-height: 180px;
  color: #697671;
  place-items: center;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .filters--compact {
    grid-template-columns: minmax(240px, 1fr) auto;
  }
}

@media (max-width: 620px) {
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .heading-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .filters {
    grid-template-columns: 1fr;
  }
}
</style>
